import { type ElementType } from "react";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import { LoadingButton } from "@mui/lab";
import bulletPointImg from "@repo/ui/assets/images/bulletXS.png";
import { InternalButtonLink } from "./InternalButtonLink";
import { MuiImage } from "./MuiImage";

export const FormCard = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        maxWidth: 445,
        p: { md: 5, xs: 3 },
        boxShadow: theme.customShadows.close,
        borderRadius: 1,
        backgroundColor: "white.main",
    }),
);

export const PageRoot = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        maxWidth: 480,
        mx: "auto",
        px: { xs: 3, md: 0 },
        py: 4,
    }),
);

export const PageTitle = styled(Typography)<{ component?: ElementType }>({
    fontWeight: 700,
});

export const BankIdButtonWrapper = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        width: "100%",
        mt: 4,
        px: { xs: 1 },
        "& > *": { width: "100%", flexWrap: "wrap" },
    }),
);

export const StyledDivider = styled(Divider)(({ theme }) =>
    theme.unstable_sx({
        my: 3,
    }),
);

export const EmailHeading = styled(Typography)(({ theme }) =>
    theme.unstable_sx({
        mb: 1,
    }),
);

export const ErrorActionButton = styled(LoadingButton)(({ theme }) =>
    theme.unstable_sx({
        width: "50%",
        py: "11px",
    }),
);

export const EmailTextField = styled(TextField)(({ theme }) =>
    theme.unstable_sx({
        mb: 2,
    }),
);

export const SubmitButton = styled(LoadingButton)(({ theme }) =>
    theme.unstable_sx({
        py: "12px",
    }),
);

export const ErrorAlert = styled(Alert)(({ theme }) =>
    theme.unstable_sx({
        mt: 2,
    }),
);

export const DividerText = styled(Typography)(({ theme }) =>
    theme.unstable_sx({
        color: "grey.dark",
        px: 3,
    }),
);

export const ActionStack = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        mt: 5,
        flexDirection: "row",
        justifyContent: "space-between",
        gap: 5,
    }),
);

export const ActionButton = styled(Button)(({ theme }) =>
    theme.unstable_sx({
        width: "50%",
        py: "11px",
    }),
);

export const ProductStack = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        marginTop: "12px",
        p: 5,
        boxShadow: theme.customShadows.close,
        borderRadius: 1,
        backgroundColor: "white.main",
    }),
);

export const ProductTextStack = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        marginBottom: "12px",
    }),
);

export const BulletListContainer = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        marginLeft: -4,
        fontSize: { xs: 14, md: 16 },
        flexWrap: "wrap",
    }),
);

export const BulletListWrapper = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "space-between",
    }),
);

export const BulletListColumn = styled(Stack)<{ width?: { xs: string; md: string } }>(({ theme, width }) =>
    theme.unstable_sx({
        width: width ?? { xs: "100%", md: "53%" },
    }),
);

export const BulletListPrimaryColumn = styled(BulletListColumn)(({ theme }) =>
    theme.unstable_sx({
        width: { xs: "100%", md: "53%" },
    }),
);

export const BulletListSecondaryColumn = styled(BulletListColumn)(({ theme }) =>
    theme.unstable_sx({
        width: { xs: "100%", md: "47%" },
    }),
);

export const BulletList = styled("ul")({
    marginTop: 0,
    listStyleImage: `url(${bulletPointImg})`,
    textWrap: "nowrap",
});

export const BulletListFull = styled("ul")({
    marginTop: 0,
    listStyleImage: `url(${bulletPointImg})`,
});

export const BulletItem = styled("li")({
    marginBottom: 4,
});

export const LoginDesktopImage = styled(MuiImage)(({ theme }) =>
    theme.unstable_sx({
        position: "absolute",
        zIndex: -1,
        left: "-95px",
        width: 824,
    }),
);

export const ProductLink = styled(InternalButtonLink)(({ theme }) =>
    theme.unstable_sx({
        width: "100%",
        textAlign: "center",
        px: { xs: 5 },
    }),
);
